<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="mt-5 filter-container"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="6" />
      <vs-col vs-w="2">
        <select-with-multiple
          class="px-2"
          placeholder="Company"
          :options="options.company"
          v-model="filters.company"
          :activate-search="true"
        />
      </vs-col>
      <vs-col vs-w="2">
        <date-picker
          class="px-2"
          type="month"
          format="MMM YYYY"
          value-type="format"
          placeholder="Month/Year"
          v-model="filters.month_year"
        />
      </vs-col>
      <vs-col
        vs-w="1"
        class="flex flex-col justify-center h-12"
      >
        <vs-button
          type="border"
          color="primary"
          class="self-center font-semibold"
          style="min-width: 100px"
          @click="resetFilters"
        >
          Reset
        </vs-button>
      </vs-col>
    </vs-row>
    <div class="main-container">
      <table-top
        is-search-has-border
        :page-size-start="false"
        class="mt-6"
        @fetch="fetchFuelSupplies()"
        ref="tabletop"
      >
        <template #start>
          <div class="flex flex-row items-center justify-center gap-4">
            <vs-button
              v-show="isUserCanAccessMenu(permission.name, 'edit') && isUserCanAccessMenu(permission.name, 'add') "
              class="button font-semibold bg-primary-accent"
              color=""
              :disabled="!filters.month_year"
              @click="isSendModalShown = true"
            >
              Send Email
            </vs-button>
            <vs-button
              ref="buttonExport"
              class="button w-min font-semibold"
              color="secondary"
              @click="isDownload = true"
            >
              Export
            </vs-button>
            <div
              v-if="isDownload"
              class="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex"
              style="z-index: 1000"
              @click.self="isDownload = false"
            >
              <div
                class="bg-white p-3 rounded shadow-xl"
                style="height: fit-content"
                :style="{
                  marginTop: `${modalExportPosition.top}px`,
                  marginLeft: `${modalExportPosition.left}px`
                }"
              >
                <div class="flex flex-col items-start gap-1">
                  <vs-button
                    class="w-full flex justify-start"
                    type="flat"
                    color="dark"
                    @click="downloadReport('bunker_report', false)"
                  >
                    <div class="flex flex-row items-start gap-2">
                      <img
                        :src="require('@/assets/shipo-icon-file-download.svg')"
                        alt="Download Daily Bunker Report"
                      >
                      <span class="font-semibold underline">
                        Daily Bunker Report.XLSX
                      </span>
                    </div>
                  </vs-button>
                  <vs-button
                    class="w-full flex justify-start"
                    type="flat"
                    color="dark"
                    @click="downloadReport('fuel_supply_report', true)"
                  >
                    <div class="flex flex-row gap-2">
                      <img
                        :src="require('@/assets/shipo-icon-file-download.svg')"
                        alt="Download Fuel Supply Report"
                      >
                      <span class="font-semibold underline">
                        Fuel Supply.XLSX
                      </span>
                    </div>
                  </vs-button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </table-top>
      <data-table
        class="mt-6"
        :table-header="tableHeaders"
        :table-data="tableData"
        :sort-by="filters.sort_by"
        :sort-dir="filters.sort_dir"
        :loading="isBusy"
        :frozen-columns="6"
        body-height="auto"
        @sort="sort"
        :grouped-headers="groupHeader"
      >
        <template #no="{ index }">
          {{ (currentPage - 1) * pageSize + index + 1 }}
        </template>
        <template #action="{ row }">
          <div
            v-if="generateRowAction(row) === 'read'"
            class="flex justify-center items-center icon-transparent-bg"
            @click="handleRead(row)"
          >
            <img :src="require('@/assets/shipo-icon-see.svg')">
          </div>
          <div
            v-if="generateRowAction(row) === 'edit_fuel' || generateRowAction(row) === 'edit_account'"
            class="flex items-center justify-center icon-transparent-bg"
            @click="handleEdit(row)"
          >
            <img :src="require('@/assets/shipo-icon-edit.svg')">
          </div>
        </template>
        <template #date="{ row }">
          <div>
            {{ row.date ? convertToDateTimeFormat(row.date, 'DD MMM YYYY') : '-' }}
          </div>
        </template>
        <template #trip_number="{ row }">
          <div>
            {{ row.trip_number || '-' }}
          </div>
        </template>
        <template #tug_name="{ row }">
          <div>
            {{ row.tug_name || '-' }}
          </div>
        </template>
        <template #barge_name="{ row }">
          <div>
            {{ row.barge_name || '-' }}
          </div>
        </template>
        <template #status="{ row }">
          <div
            :class="`px-3 badge
              ${ row.status.toLowerCase() === FUEL_SUPPLY_STATUS.COMPLETE
                 ? 'complete'
                 : row.status.toLowerCase() === FUEL_SUPPLY_STATUS.WAITING_FUEL_DETAIL
                 ? 'fuel-detail'
                 : 'account-detail'
            }`"
          >
            {{ row.status || '-' }}
          </div>
        </template>
        <template #horse_power="{ row }">
          <div>
            {{ row.horse_power ? handleValidateAndFormatNumber(row.horse_power) : 0 }}
          </div>
        </template>
        <template #trip_before_route_name="{ row }">
          <span
            v-if="!row.trip_before_route_name"
            class="text-danger font-semibold"
          >
            * required to fill
          </span>
          <div v-else>
            {{ row.trip_before_route_name }}
          </div>
        </template>
        <template #quota_in_liters="{ row }">
          <div>
            {{ row.quota_in_liters ? handleValidateAndFormatNumber(row.quota_in_liters) : 0 }}
          </div>
        </template>
        <template #last_trip_route_name="{ row }">
          <span
            v-if="!row.last_trip_route_name"
            class="text-danger font-semibold"
          >
            * required to fill
          </span>
          <div v-else>
            {{ row.last_trip_route_name || '-' }}
          </div>
        </template>
        <template #actual_route="{ row }">
          <div>
            {{ row.actual_route || '-' }}
          </div>
        </template>
        <template #fuel_liters_rob_before="{ row }">
          <span
            v-if="!row.fuel_liters_rob_before"
            class="text-danger font-semibold"
          >
            * required to fill
          </span>
          <div v-else>
            {{ formatQuantity(row.fuel_liters_rob_before) }}
          </div>
        </template>
        <template #fuel_liters_supplied_at_smd="{ row }">
          <span
            v-if="!row.fuel_liters_supplied_at_smd"
            class="text-danger font-semibold"
          >
            * required to fill
          </span>
          <div v-else>
            {{ formatQuantity(row.fuel_liters_supplied_at_smd) }}
          </div>
        </template>
        <template #fuel_liters_supplied_at_boct="{ row }">
          <div>
            {{ formatQuantity(row.fuel_liters_supplied_at_boct) || '-' }}
          </div>
        </template>
        <template #fuel_liters_rob_after="{ row }">
          <div>
            {{ formatQuantity(countRobAfter(row)) }}
          </div>
        </template>
        <template #arrived_dt_smd="{ row }">
          <div>
            {{ convertToDateTimeFormat(row.arrived_dt_smd) }}
          </div>
        </template>
        <template #arrived_dt_bunker="{ row }">
          <span
            v-if="!row.arrived_dt_bunker"
            class="text-danger font-semibold"
          >
            * required to fill
          </span>
          <div v-else>
            {{ convertToDateTimeFormat(row.arrived_dt_bunker) }}
          </div>
        </template>
        <template #checkpoint_waiting_hours="{ row }">
          <div>
            {{ countHoursDiff(row.arrived_dt_smd, row.arrived_dt_bunker) }}
          </div>
        </template>
        <template #move_to_tugboat_lct_alongside="{ row }">
          <span
            v-if="!row.move_to_tugboat_lct_alongside"
            class="text-danger font-semibold"
          >
            * required to fill
          </span>
          <div v-else>
            {{ convertToDateTimeFormat(row.move_to_tugboat_lct_alongside) }}
          </div>
        </template>
        <template #move_to_tugboat_total_hours="{ row }">
          <div>
            {{ countHoursDiff(row.arrived_dt_bunker, row.move_to_tugboat_lct_alongside) }}
          </div>
        </template>
        <template #bunkering_start_dt="{ row }">
          <div>
            {{ convertToDateTimeFormat(row.bunkering_start_dt) }}
          </div>
        </template>
        <template #bunkering_stop_dt="{ row }">
          <div>
            {{ convertToDateTimeFormat(row.bunkering_stop_dt) }}
          </div>
        </template>
        <template #bunkering_hours="{ row }">
          <div>
            {{ countHoursDiff(row.bunkering_start_dt, row.bunkering_stop_dt) }}
          </div>
        </template>
        <template #departure_dt="{ row }">
          <div>
            {{ convertToDateTimeFormat(row.departure_dt) }}
          </div>
        </template>
        <template #remarks="{ row }">
          <div>
            {{ row.remarks || '-' }}
          </div>
        </template>
        <template #evidence_attachments="{ row }">
          <span
            v-if="!row.evidence_attachments"
            class="text-danger font-semibold"
          >
            * required to fill
          </span>
          <vs-button
            v-else
            class="bg-secondary-accent text-secondary py-1 w-64 h-8"
            color="primary"
            type="button"
            :disabled="!(row.evidence_attachments || (row.evidence_attachments && row.evidence_attachments.url))"
            @click="openDocumentModal(row)"
          >
            <div class="flex flex-row items-center justify-start pr-2 w-full">
              <img
                :src="require('@/assets/shipo-icon-see.svg')"
                class="flex-shrink-0 mr-2 h-6"
              >
              <div class="w-32 truncate">
                {{ row.evidence_attachments ? row.evidence_attachments.name : 'No Photo' }}
              </div>
            </div>
          </vs-button>
        </template>
      </data-table>
      <table-bottom
        class="mt-6"
        @fetch="fetchFuelSupplies()"
      />
    </div>
    <base-custom-modal
      class="document-modal"
      v-if="isDocumentModalShown"
      :is-shown="isDocumentModalShown"
      @update:isShown="isDocumentModalShown=$event"
    >
      <template #title>
        <span class="font-semibold">Receipt Bunker</span>
      </template>
      <template #body>
        <div
          v-if="selectedDocument.type.startsWith('image') || selectedDocument.type.startsWith('application/pdf')"
          class="self-center flex flex-col items-center justify-center max-w-lg mx-auto"
        >
          <img
            v-if="selectedDocument.thumbnail"
            :src="selectedDocument.thumbnail"
            alt="Barge Photo"
            class="w-2/3 h-auto object-contain rounded-3xl border-2"
          >
          <span class="mt-5 w-full truncate text-center"> {{ selectedDocument.name }} </span>
          <vs-button
            class="mt-8 py-4 w-1/2"
            color="secondary"
            @click="downloadFile"
          >
            Download File
          </vs-button>
        </div>
        <div
          v-else
          class="self-center flex flex-col items-center justify-center max-w-lg mx-auto"
        >
          <img
            v-if="selectedDocument.thumbnail"
            :src="selectedDocument.thumbnail"
            alt="Barge Photo"
            class="w-3/4 h-auto object-contain rounded-3xl border-2"
          >
          <span class="mt-5 w-full truncate text-center"> {{ selectedDocument.name }} </span>
          <vs-button
            class="mt-8 py-4 w-1/2"
            color="secondary"
            @click="downloadFile"
          >
            Download File
          </vs-button>
        </div>
      </template>
    </base-custom-modal>
    <base-custom-modal
      v-if="isFormModalShown"
      :is-shown="isFormModalShown"
      @update:isShown="isFormModalShown=$event"
    >
      <template #body>
        <fuel-supply-form
          ref="editFormComponent"
          :is-processing="isProcessing"
          :access-type="generateRowAction(currentRow)"
          :row-data="currentRow"
          :route-options="options.routes"
          :company-options="options.company"
          :house-power-routes="options.horse_power_routes"
        />
      </template>
      <template
        #footer
        v-if="generateRowAction(currentRow) !== 'read'"
      >
        <div class="flex flex-row justify-end">
          <vs-button
            ref="editButton"
            type="filled"
            color="primary"
            :class="`w-32 mr-2 font-semibold ${isProcessing && 'cursor-not-allowed opacity-50 animate-bounce'}`"
            @click="handleSubmit"
          >
            Save Data
          </vs-button>
          <vs-button
            type="border"
            color="primary"
            :class="`w-32 ml-2 font-semibold ${isProcessing && 'cursor-not-allowed opacity-50 animate-bounce'}`"
            @click="isFormModalShown = false"
          >
            Cancel
          </vs-button>
        </div>
      </template>
    </base-custom-modal>
    <send-email-modal
      :active.sync="isSendModalShown"
      :filters="filters"
      :is-modal-shown="isSendModalShown"
      @close="() => isSendModalShown = false"
    />
  </div>
</template>

<script>
import moment from "moment/moment";
import { mapGetters } from 'vuex'

import BaseCustomModal from "@/components/baseCustomModal";
import DataTable from '@/components/dataTable'
import DatePicker from '@/components/datePicker'
import SelectWithMultiple from '@/components/selectWithMultiple'
import TableBottom from "@/components/tableBottom";
import TableTop from '@/components/tableTop'
import { PAGE_TITLE } from '@/fixtures/Constants'
import downloadUtils from "@/utils/downloadUtils";
import FuelSupplyForm from "@/views/reports/fuelSupplyManagement/createDetailEdit/index.vue";
import SendEmailModal from '@/views/reports/fuelSupplyManagement/modalSendEmail/index.vue'

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    SendEmailModal,
    FuelSupplyForm,
    BaseCustomModal,
    TableBottom,
    DataTable,
    DatePicker,
    SelectWithMultiple,
    TableTop
  },
  data() {
    return {
      isBusy: false,
      isDownload: false,
      isDocumentModalShown: false,
      isFormModalShown: false,
      isSendModalShown: false,
      filters: {
        sort_by: null,
        sort_dir: null,
        month_year: null,
        company: []
      },
      options: {
        company: [],
        routes: [],
        horse_power_routes: []
      },
      FUEL_SUPPLY_STATUS: {
        WAITING_FUEL_DETAIL: 'waiting for fuel detail',
        WAITING_ACCOUNT_DETAIL: 'waiting for account detail',
        COMPLETE: 'completed'
      },
      tableHeaders: [
        { key: 'no', label: 'No', width: '50px' },
        { key: 'action', label: 'Action', width: '50px' },
        { key: 'date', label: 'Date', sortable: true },
        { key: 'trip_number', label: 'Trip No', sortable: true },
        { key: 'tug_name', label: 'Tug Name', sortable: true },
        { key: 'barge_name', label: 'Barge Name', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'horse_power', label: 'Horse Power', sortable: true, group: 'Fuel Supply Base on' },
        { key: 'trip_before_route_name', label: 'Trip (Before)', sortable: true, group: 'Fuel Supply Base on' },
        { key: 'quota_in_liters', label: 'Quota', sortable: true, group: 'Fuel Supply Base on' },
        { key: 'last_trip_route_name', label: 'Last Trip Route', sortable: true, group: 'Last Trip Supplied for Route' },
        { key: 'actual_route', label: 'Actual Route', sortable: true, group: 'Last Trip Supplied for Route' },
        { key: 'fuel_liters_rob_before', label: 'ROB Before', sortable: true, group: 'Bunker Report' },
        { key: 'fuel_liters_supplied_at_smd', label: 'Supplied at SMD', sortable: true, group: 'Bunker Report' },
        { key: 'fuel_liters_supplied_at_boct', label: 'Supplied at BoCT', sortable: true, group: 'Bunker Report' },
        { key: 'fuel_liters_rob_after', label: 'ROB After', sortable: true, group: 'Bunker Report' },
        { key: 'arrived_dt_smd', label: 'Arrived at Samarinda', sortable: true },
        { key: 'arrived_dt_bunker', label: 'Arrived at Bunker Station', sortable: true },
        { key: 'checkpoint_waiting_hours', label: 'Waiting Time', sortable: true },
        { key: 'move_to_tugboat_lct_alongside', label: 'LCT Alongside', sortable: true, group: 'Move to tug boat' },
        { key: 'move_to_tugboat_total_hours', label: 'Total (Hours)', sortable: true, group: 'Move to tug boat' },
        { key: 'bunkering_start_dt', label: 'Start', sortable: true, group: 'Time Bunkering' },
        { key: 'bunkering_stop_dt', label: 'End', sortable: true, group: 'Time Bunkering' },
        { key: 'bunkering_hours', label: 'Total (Hours)', sortable: true, group: 'Time Bunkering' },
        { key: 'departure_dt', label: 'Departure', sortable: true },
        { key: 'remarks', label: 'Remarks' },
        { key: 'evidence_attachments', label: 'Document' }
      ],
      tableData: [],
      selectedDocument: null,
      isEditing: false,
      currentRow: null,
      isDownloadFuelSupply: false,
      isProcessing: false
    }
  },
  computed: {
    ...mapGetters({
      fuelSupplies: 'FuelSupplyReport/fuelSupplies',
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      companies: 'Companies/companies',
      routes: 'FuelSupplyTugboat/routes',
      fuelSuppliesFiles: 'FuelSupplyReport/fuelSuppliesFiles',
      fuelSupplyTugboats: 'FuelSupplyTugboat/fuelSupplyTugboats'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    groupHeader() {
      const BACK_GROUND_COLOR = [
        {
          title: 'Fuel Supply Base on',
          backgroundColor: '#FEF3F0'
        },
        {
          title: 'Last Trip Supplied for Route',
          backgroundColor: '#DFDBEF'
        },
        {
          title: 'Bunker Report',
          backgroundColor: '#D1F9E3'
        },
        {
          title: 'Move to tug boat',
          backgroundColor: '#FFD9D9'
        },
        {
          title: 'Time Bunkering',
          backgroundColor: '#DFDBEF'
        }
      ]
      return this.tableHeaders
        .filter(header => !!header.group)
        .reduce((acc, item) => {
          if (!acc.some(header => header.title === item.group)) {
            acc.push({
              title: item.group,
              startColumn: item.key,
              backgroundColor: BACK_GROUND_COLOR.find(background => background.title === item.group).backgroundColor
            })
          } else {
            acc.find(header => header.title === item.group)['endColumn'] = item.key
          }
          return acc
        }, [])
    },
    modalExportPosition() {
      const el = this.$refs.buttonExport?.$el || this.$refs.buttonExport
      if (!el) {
        return { top: 0, left: 0 }
      }

      const { offsetTop, offsetLeft, offsetHeight } = el
      return {
        top: offsetTop + offsetHeight,
        left: offsetLeft
      };
    }
  },
  mounted() {
    this.fetchCompanies()
    this.fetchRoutes()
    this.fetchFuelSupplies()
    this.fetchAllFuelSupplyTugboats()
  },
  watch: {
    fuelSupplies: {
      handler(val) {
        if (val) {
          this.tableData = val.map((item) => ({
            ...item,
            evidence_attachments: item.evidence_attachments?.[0]
          }))
        }
      }
    },
    companies: {
      handler(val) {
        if (val) {
          this.options.company = val.map(item => ({
            value: item.code,
            text: item.code,
            id: item.id
          }))
        }
      }
    },
    routes: {
      handler(val) {
        if (val) {
          this.options.routes = val.map((item) => ({
            value: item.alias,
            text: item.alias
          }))
        }
      }
    },
    filters: {
      deep: true,
      handler() {
        this.fetchFuelSupplies()
      }
    },
    fuelSuppliesFiles: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(val, this.isDownloadFuelSupply ? 'Fuel_supply_report.xlsx' : 'Daily_bunker_report.xlsx')
          // Reset the companiesFiles state
          this.$store.dispatch('FuelSupplyReport/resetFuelSuppliesFiles')
        }
      }
    },
    fuelSupplyTugboats: {
      handler(val) {
        if (val) {
          this.options.horse_power_routes = val.map((item) => ({
            value: item.id,
            text: item.route_name,
            details: item.fuel_supply_route_details
          }))
        }
      }
    }
  },
  methods: {
    generateRowAction(row) {
      const status = row.status.toLowerCase()
      if (status === this.FUEL_SUPPLY_STATUS.COMPLETE) {
        return 'read'
      }

      const permissionType = status === this.FUEL_SUPPLY_STATUS.WAITING_FUEL_DETAIL
        ? 'edit_fuel'
        : 'edit_account'

      const canAdd = this.isUserCanAccessMenu(this.permission.name, 'add')
      const canEdit = this.isUserCanAccessMenu(this.permission.name, 'edit')

      if (canAdd) return permissionType
      if (!canAdd && canEdit && permissionType === 'edit_account') return permissionType

      return 'read'
    },
    async fetchCompanies() {
      try {
        await this.$store.dispatch('Companies/fetchAllCompanies', {
          is_master_data: true,
          type: 'SITE_OWNER',
          status: true,
          sort_dir: 'asc',
          sort_by: 'name'
        })
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch companies, please try again')
      }
    },
    async fetchRoutes() {
      try {
        const params = {
          type_port: 'POL,POD'
        }
        await this.$store.dispatch('FuelSupplyTugboat/fetchRoutesWithType', params)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Routes, please try again')
      }
    },
    async fetchAllFuelSupplyTugboats() {
      try {
        const params = {
          status: 'ACKNOWLEDGED'
        }
        await this.$store.dispatch('FuelSupplyTugboat/fetchAllFuelSupplyTugboats', params)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Routes based on horse power, please try again')
      }
    },
    async fetchFuelSupplies() {
      try {
        this.isBusy = true
        const apiFilters = Object.keys(this.filters)
          .filter(key => !!this.filters[key] && !!this.filters[key]?.length)
          .reduce((item, key) => {
            if (key === 'company') {
              item[key] = this.filters[key].map(company => company.value).join(',')
            } else if (key === 'month_year') {
              item[key] = moment(this.filters[key]).format('YYYY-MM')
            } else {
              item[key] = this.filters[key]
            }
            return item
          }, {})

        if (!apiFilters?.sort_by && !apiFilters?.sort_dir) {
          apiFilters['sort_by'] = 'status'
          apiFilters['sort_dir'] = 'desc'
        }

        await this.$store.dispatch(
          'FuelSupplyReport/fetchFuelSupplyReports',
          apiFilters
        )
        this.isBusy = false
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch fuel supply report, please try again')
        this.isBusy = false
      }
    },
    sort(sortBy, sortDir) {
      this.filters.sort_by = sortBy
      this.filters.sort_dir = sortDir

      this.fetchFuelSupplies()
    },
    resetFilters() {
      this.filters = {
        date: null,
        company: []
      }

      this.$refs.tabletop.$emit('reset-search')
    },
    countRobAfter(row) {
      return row?.fuel_liters_rob_before + row?.fuel_liters_supplied_at_smd + row?.fuel_liters_supplied_at_boct
    },
    countHoursDiff(startTime, endTime) {
      const start = moment.utc(this.convertToDateTimeFormat(startTime)).local()
      const end = moment.utc(this.convertToDateTimeFormat(endTime)).local()

      if (!start.isValid() || !end.isValid()) {
        return '0.00'
      }

      const diffInHours = end.diff(start, 'minutes') / 60

      return isNaN(diffInHours) ? 'N/A' : diffInHours.toFixed(2)
    },
    convertToDateTimeFormat(dateTime, format = 'DD MMM YYYY HH:mm') {
      return dateTime ? moment.utc(dateTime).local().format(format) : '-'
    },
    formatQuantity(qty) {
      return qty ? `${ this.handleValidateAndFormatNumber(qty) } Liter` : '-'
    },
    openDocumentModal(row) {
      if (row.evidence_attachments && row.evidence_attachments?.thumbnail) {
        this.selectedDocument = row.evidence_attachments
        this.isDocumentModalShown = true
      }
    },
    handleRead(row) {
      this.isEditing = false
      this.currentRow = JSON.parse(JSON.stringify(row))
      this.isFormModalShown = true
    },
    handleEdit(row) {
      this.isEditing = true
      this.currentRow = JSON.parse(JSON.stringify(row))
      this.isFormModalShown = true
    },
    async handleSubmit() {
      try {
        const editForm = this.$refs.editFormComponent
        const form = editForm.getFormData()
        if (!form) {
          return
        }

        const { id } = this.currentRow
        this.isProcessing = true
        await this.$store.dispatch('FuelSupplyReport/putFuelSupplyReport', {
          id,
          form
        })

        this.showSuccessNotification(this, 'Fuel supply successfully updated.')

        this.isEditing = false
        this.isFormModalShown = false
        await this.fetchFuelSupplies()
        this.isProcessing = false
      } catch ({ error }) {
        this.isProcessing = false
        this.showFailedNotification(
          this,
          error?.message || 'Failed to save changes, please try again',
          'Error'
        )
      }
    },
    async downloadReport(reportType, isFuelSupply) {
      try {
        this.isBusy = true
        this.isDownloadFuelSupply = isFuelSupply
        const apiFilters = Object.keys(this.filters)
          .filter(key => !!this.filters[key] && !!this.filters[key]?.length)
          .reduce((item, key) => {
            if (key === 'company') {
              item[key] = this.filters[key].map(company => company.value).join(',')
            } else if (key === 'month_year') {
              item[key] = moment(this.filters[key]).format('MM-YYYY')
            } else {
              item[key] = this.filters[key]
            }
            return item
          }, { type: reportType })


        if (!this.filters.month_year) {
          apiFilters['month_year'] = moment(new Date()).format('MM-YYYY')
        }

        await this.$store.dispatch(
          'FuelSupplyReport/downloadFuelSupplyReport',
          apiFilters,
          {
            headers: {
              accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            responseType: 'blob'
          }
        )
        this.isBusy = false;
        this.isDownload = false;
      } catch (error) {
        this.isDownload = false
        this.isBusy = false;
        this.showFailedNotification(this, 'Failed to download report, please try again');
      }
    },
    downloadFile() {
      const link = document.createElement('a');
      link.href = this.selectedDocument.downloadUrl;
      link.download = this.selectedDocument.name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-container {
  @apply w-full p-5 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.main-container {
  @apply p-5 flex flex-col mt-5 bg-white rounded-md;
}

.button {
  @apply py-4;
  @apply rounded-lg;
  @apply border-2;
  @apply font-semibold;
  @apply min-w-button;
  @apply text-base;
  @apply bg-primary-accent;
  height: calc(1rem * 10 / 3);

  &.shadow-secondary {
    -webkit-box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary), 1);
    box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary), 1);
  }

  &:focus {
    @apply outline-none;
  }
}

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply min-w-8;
  @apply h-8;
  background-color: #493493;
  border-radius: 0.5rem;

  &.complete {
    background-color: #d1f9e3;
  }

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

.badge {
  font-weight: 500;
  text-align: center;
  padding: 5px 0;
  border-radius: 5px;

  &.complete {
    background-color: rgba(76, 175, 80, 0.2);
    color: #4CAF50;
  }

  &.fuel-detail {
    background-color: rgba(251, 178, 51, 0.2);
    color: #F9BF59;
  }

  &.account-detail {
    background-color: rgba(90, 76, 175, 0.2);
    color: #5A4CAF;
  }
}

/deep/ .vs-button-dark {
  &.isActive {
    img {
      filter: brightness(0) invert(1);
    }
  }
}

.document-modal {
  /deep/ .modal-content-container {
    width: fit-content;
    height: fit-content;
  }
}
</style>
